import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./DataEntryPage.css";
import { API_BASE } from "../api/config";

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));
const waitForExcelReady = async (sessionId, tries = 600, delay = 500) => {
  for (let i = 0; i < tries; i++) {
    try {
      const r = await fetch(`${API_BASE}/session/data`, {
        headers: { "Session-ID": sessionId, "Cache-Control": "no-cache" },
      });
      if (r.ok) {
        const d = await r.json();
        if (d && d.excel) return d;
      }
    } catch (_) {}
    await sleep(delay);
  }
  return null;
};

const DataEntryPage = ({ triggerUpdate }) => {
  const [excelSheets, setExcelSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [importantDate, setImportantDate] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [excelMulti, setExcelMulti] = useState([]); // Liste für Massen-Excel
  const [sheetOverride, setSheetOverride] = useState({}); // fileName -> ausgewähltes Sheet
  const [isDragging, setIsDragging] = useState(false);
  const navigate = useNavigate();

  // Prüfen, ob für alle Dateien bereits ein Sheet festgelegt ist
  const areAllSheetsChosen = (list) => {
    if (!Array.isArray(list)) return false;
    return list.length > 0 && list.every((row) => {
      const fileName = row.fileName || row.name || "";
      const chosen = (sheetOverride[fileName]) || row.chosenSheet || "";
      const needs = row.needsUserSheet === true; // nur TRUE blockiert; undefined/false heißt: kein Nutzer-Eingriff nötig
      const sheets = Array.isArray(row.sheets) ? row.sheets : [];
      const hasValidChoice = !!chosen && (sheets.length === 0 || sheets.includes(chosen));
      return !needs && hasValidChoice;
    });
  };

  // Beim Laden der Seite Session-ID aus LocalStorage holen
  useEffect(() => {
    const session = localStorage.getItem("sessionId");
    if (!session) {
      alert("Keine aktive Sitzung gefunden. Bitte starten Sie eine neue Sitzung.");
      navigate("/");
    } else {
      setSessionId(session);
    }
  }, [navigate]);

  // Excel-Datei hochladen & Arbeitsblätter abrufen
  const handleExcelImport = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      alert("Bitte wählen Sie eine Excel-Datei aus.");
      return;
    }

    setLoading(true); // ⏳ Ladeanzeige aktivieren

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API_BASE}/excel/upload`, {
        method: "POST",
        body: formData,
        headers: { "Session-ID": sessionId },
      });

      if (!response.ok) {
        const errorData = await response.json();
        alert(errorData.detail || "Fehler beim Hochladen der Excel-Datei.");
        return;
      }

      const data = await response.json();
      setExcelSheets(data.sheets || []);

      // Automatische Vorauswahl eines Sheets, das "Daten" enthält
      const defaultSheet = data.sheets.find(sheet => sheet.toLowerCase().includes("daten")) || "";
      setSelectedSheet(defaultSheet);
      
    } catch (error) {
      console.error("Fehler beim Laden der Excel-Datei:", error);
      alert("Fehler beim Laden der Excel-Datei.");
    } finally {
      setLoading(false); // ✅ Ladeanzeige deaktivieren
    }
  };

  // Arbeitsblattdaten abrufen
  const handleSheetSelection = async () => {
    if (!selectedSheet) {
      alert("Bitte wählen Sie ein Arbeitsblatt aus.");
      return;
    }
  
    setLoading(true); // Ladeanzeige aktivieren
  
    try {
      const formData = new FormData();
      formData.append("sheet", selectedSheet);
  
      const response = await fetch(`${API_BASE}/excel/get_sheet_data`, {
        method: "POST",
        body: formData,
        headers: { "Session-ID": sessionId },
      });
  
      const data = await response.json();
  
      if (response.ok) {
        setAccountNumber(data.accountNumber);
        setImportantDate(data.newestDate);
        await waitForExcelReady(sessionId);
        if (triggerUpdate) {
          console.log("🔄 triggerUpdate wird ausgelöst nach Arbeitsblatt-Auswahl!");
          triggerUpdate(); // ✅ Zeigt MappingPage an
        }
        try { localStorage.setItem('navModeKey','1'); localStorage.setItem('preferredViewMode','single'); } catch(_) {}
      } else {
        alert(data.detail || "Fehler beim Abrufen der Arbeitsblattdaten.");
      }
    } catch (error) {
      console.error("Fehler beim Abrufen der Arbeitsblattdaten:", error);
      alert("Fehler beim Abrufen der Arbeitsblattdaten.");
    } finally {
      setLoading(false); // Ladeanzeige deaktivieren
    }
  };

  // =========================
  // Mehrere Excel (Massenverarbeitung)
  // =========================
  const handleMultiExcelImport = async (event) => {
    const all = Array.from(event.target.files || []);
    const files = all.filter(f => /\.xlsx$/i.test(f.name) && !f.name.startsWith('~$'));
    if (!files.length) {
      alert("Bitte wählen Sie Excel-Dateien oder einen Ordner mit Excel-Dateien aus.");
      return;
    }

    // Optimistische Anzeige: Zeige die gewählten Dateien sofort in der Tabelle
    const optimistic = files.map((f) => ({
      fileName: f.name,
      sheets: [],
      chosenSheet: "",
      needsUserSheet: true,
      accountNumber: "",
      newestDate: "",
      status: "Wird hochgeladen...",
    }));
    setExcelMulti(optimistic);

    setLoading(true);
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append("files", f));
      const resp = await fetch(`${API_BASE}/excel/multi/upload`, {
        method: "POST",
        body: formData,
        headers: { "Session-ID": sessionId },
        cache: "no-store",
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Fehler beim Hochladen der Excel-Dateien.");
      }
      const data = await resp.json();
      // Backend liefert typischerweise { files: [...] }
      let list = Array.isArray(data?.files) ? data.files : (Array.isArray(data) ? data : []);
      if (!Array.isArray(list)) list = [];

      if (list.length > 0) {
        // Normale Rückgabe: direkt anzeigen
        setExcelMulti(list);
      } else {
        // Fallback: einmalig Liste vom Server nachladen (keine Schleife)
        try {
          const refreshed = await refreshExcelList();
          if (Array.isArray(refreshed) && refreshed.length > 0) {
            // refreshExcelList setzt bereits den State, aber zur Klarheit erneut setzen
            setExcelMulti(refreshed);
          } else {
            // Nichts vom Server? Dann die optimistische Ansicht beibehalten
            setExcelMulti((prev) => (prev && prev.length ? prev : optimistic));
          }
        } catch (_) {
          // Bei Fehlern die optimistische Ansicht nicht verwerfen
          setExcelMulti((prev) => (prev && prev.length ? prev : optimistic));
        }
      }
      // (Kein zusätzliches Auto-Refresh / keine Polling-Schleifen – verhindert Flackern)
    } catch (e) {
      console.error(e);
      alert(e.message || "Fehler beim Hochladen der Excel-Dateien.");
    } finally {
      setLoading(false);
      // Reset des Inputs ermöglichen (gleiche Dateien erneut wählen)
      event.target.value = "";
    }
  };

  const refreshExcelList = useCallback(async () => {
    if (!sessionId) return [];
    try {
      const resp = await fetch(`${API_BASE}/excel/multi/list`, {
        headers: { "Session-ID": sessionId },
      });
      if (!resp.ok) throw new Error("Konnte Liste nicht abrufen");
      const data = await resp.json();
      const list = Array.isArray(data.files) ? data.files : (Array.isArray(data) ? data : []);
      setExcelMulti(list);
      return list;
    } catch (e) {
      console.error(e);
      alert(e.message || "Fehler beim Abrufen der Excel-Liste.");
      return [];
    }
  }, [sessionId]);

  // Auto: Liste aktualisieren sobald Session bekannt
  useEffect(() => {
    if (sessionId) {
      (async () => {
        try { await refreshExcelList(); } catch (_) {}
      })();
    }
  }, [sessionId, refreshExcelList]);

  const onRowSheetChange = (fileName, newSheet) => {
    setSheetOverride((prev) => ({ ...prev, [fileName]: newSheet }));
    if (newSheet) {
      // sofort übernehmen, kein Extra-Button nötig
      applySheetForFile(fileName, newSheet);
    }
  };

  const applySheetForFile = async (fileName, overrideSheet = null) => {
    const sheet = overrideSheet ?? sheetOverride[fileName];
    if (!sheet) {
      alert("Bitte wählen Sie ein Arbeitsblatt aus.");
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file_name", fileName);
      formData.append("sheet", sheet);
      const resp = await fetch(`${API_BASE}/excel/multi/set_sheet`, {
        method: "POST",
        body: formData,
        headers: { "Session-ID": sessionId },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Fehler beim Setzen des Sheets.");
      }
      const list = await refreshExcelList();
      // (Entfernt: if (areAllSheetsChosen(list)) { ... })
    } catch (e) {
      console.error(e);
      alert(e.message || "Fehler beim Setzen des Sheets.");
    } finally {
      setLoading(false);
    }
  };

  // === Batch Proceed: Übernehmen Button Handler ===
  const handleBatchProceed = async () => {
    try { localStorage.setItem('navModeKey','2'); localStorage.setItem('preferredViewMode','batch'); } catch(e) {}
    if (!sessionId) {
      alert('Keine Session-ID – bitte neu starten.');
      return;
    }
    try {
      const r = await fetch(`${API_BASE}/excel/multi/ready`, {
        headers: { 'Session-ID': sessionId, 'Cache-Control': 'no-cache' },
      });
      if (!r.ok) {
        alert('Fehler beim Prüfen der Excel-Auswahl.');
        return;
      }
      const j = await r.json();
      if (j.ready) {
        // Liste abrufen und erste Datei mit gewähltem Sheet aktivieren
        const listResp = await fetch(`${API_BASE}/excel/multi/list`, {
          headers: { 'Session-ID': sessionId },
        });
        const listJson = await listResp.json();
        const files = Array.isArray(listJson.files) ? listJson.files : [];
        const first = files.find(x => x && x.chosenSheet);
        if (!first) {
          alert('Kein Eintrag mit gewähltem Sheet gefunden.');
          return;
        }
        const fd = new FormData();
        fd.append('file_name', first.fileName);
        const act = await fetch(`${API_BASE}/excel/multi/activate`, {
          method: 'POST',
          headers: { 'Session-ID': sessionId },
          body: fd,
        });
        if (!act.ok) {
          const err = await act.json().catch(() => ({}));
          alert(err.detail || 'Fehler beim Aktivieren der Excel.');
          return;
        }
        try { await waitForExcelReady(sessionId); } catch (_) {}
        if (typeof triggerUpdate === 'function') {
          try { triggerUpdate(); } catch (_) {}
        }
        // kein Navigieren – Mapping blendet sich inline ein
      } else {
        const hint = j.missing && j.missing.length
          ? `Fehlend: ${j.missing.slice(0, 5).join(', ')}${j.missing.length > 5 ? ' …' : ''}`
          : 'Bitte alle Sheets wählen.';
        alert(`Noch nicht bereit. ${hint}`);
      }
    } catch (e) {
      console.error('Ready-Check Fehler:', e);
      alert('Netzwerkfehler beim Prüfen.');
    }
  };

  // Prüfen ob Ergebnisse vorhanden sind
  const hasResults = accountNumber || importantDate || excelMulti.length > 0 || excelSheets.length > 0;

  // Drag & Drop Handler für Excel
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDropSingle = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    const excelFiles = files.filter(f => f.name.toLowerCase().endsWith('.xlsx') && !f.name.startsWith('~$'));
    
    if (excelFiles.length === 1) {
      // Simuliere ein Event-Objekt für handleExcelImport
      const fakeEvent = { target: { files: excelFiles } };
      handleExcelImport(fakeEvent);
    } else if (excelFiles.length > 1) {
      const fakeEvent = { target: { files: excelFiles } };
      handleMultiExcelImport(fakeEvent);
    } else {
      alert("Bitte nur Excel-Dateien (.xlsx) ablegen.");
    }
  }, [sessionId]);

  const handleDropMulti = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    const excelFiles = files.filter(f => f.name.toLowerCase().endsWith('.xlsx') && !f.name.startsWith('~$'));
    
    if (excelFiles.length >= 1) {
      const fakeEvent = { target: { files: excelFiles } };
      handleMultiExcelImport(fakeEvent);
    } else {
      alert("Bitte nur Excel-Dateien (.xlsx) ablegen.");
    }
  }, [sessionId]);

  return (
    <div className={`data-entry ${hasResults ? 'has-results' : ''}`}>
      {/* Lade-Pop-up */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-box">
            <div className="loading-spinner"></div>
            <p>Daten werden geladen...</p>
          </div>
        </div>
      )}

      <div className="content-container">
        {/* Linke Seite (Buttons & Dropdown) */}
        <div className="left-container2">
          <div className="button-container">
            <label 
              className={`styled-button drop-zone ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDropSingle}
            >
              Excel-Datei
              <input type="file" onChange={handleExcelImport} accept=".xlsx" className="hidden-input" />
            </label>
            <label 
              className={`styled-button drop-zone ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDropMulti}
            >
              Ordner mit Excel
              <input
                type="file"
                multiple
                onChange={handleMultiExcelImport}
                webkitdirectory=""
                mozdirectory=""
                directory=""
                className="hidden-input"
              />
            </label>

            {/* Dropdown für Arbeitsblätter (nur anzeigen, wenn Excel geladen wurde) */}
            {excelSheets.length > 0 && (
              <select
                value={selectedSheet}
                onChange={(e) => setSelectedSheet(e.target.value)}
                className="sheet-dropdown"
              >
                {excelSheets.map((sheet, index) => (
                  <option key={index} value={sheet}>{sheet}</option>
                ))}
              </select>
            )}

            {/* Daten abrufen Button nur anzeigen, wenn eine Datei geladen wurde */}
            {excelSheets.length > 0 && (
              <button className="select-button" onClick={handleSheetSelection}>
                Daten abrufen
              </button>
            )}
          </div>
        </div>

        {/* Trennlinie */}
        <div className="divider"></div>

        {/* Rechte Seite (Ergebnisse) */}
        <div className="right-container">
          {(accountNumber || importantDate) && (
            <div className="sheet-results">
              <h2>Ergebnisse aus dem Arbeitsblatt</h2>
              <p><strong>Auftragskonto:</strong> {accountNumber || "Nicht gefunden"}</p>
              <p><strong>Wichtiges Datum:</strong> {importantDate || "Kein Datum gefunden"}</p>
            </div>
          )}
          {excelMulti.length > 0 && (
            <div className="multi-excel-section">
              <h2 style={{ marginTop: "24px" }}>Mehrere Excel (Massenverarbeitung)</h2>
              <div className="table-wrapper compact" style={{ fontSize: "12px", lineHeight: 1.15 }}>
                <table className="data-table compact" style={{ fontSize: "12px" }}>
                  <thead>
                    <tr>
                      <th>Datei</th>
                      <th>Sheet</th>
                      <th>Auftragskonto</th>
                      <th>Datum</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {excelMulti.map((row, idx) => {
                      const fileName = row.fileName || row.name || `Datei_${idx+1}`;
                      const sheets = Array.isArray(row.sheets) ? row.sheets : [];
                      const chosen = sheetOverride[fileName] || row.chosenSheet || "";
                      const needs = !!row.needsUserSheet;
                      return (
                        <tr key={fileName + idx}>
                          <td title={fileName} style={{maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{fileName}</td>
                          <td>
                            {needs ? (
                              <select
                                className="sheet-dropdown compact"
                                value={chosen}
                                onChange={(e) => onRowSheetChange(fileName, e.target.value)}
                                style={{ maxWidth: 160 }}
                              >
                                <option value="">— Sheet wählen —</option>
                                {sheets.map((s) => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>
                            ) : (
                              <span className="pill ok" title="aus Mapping gewählt">{chosen || "—"}</span>
                            )}
                          </td>
                          <td style={{whiteSpace:'nowrap'}}>{row.accountNumber || "—"}</td>
                          <td style={{whiteSpace:'nowrap'}}>{row.newestDate || "—"}</td>
                          <td>{row.status || (needs ? "Sheet wählen" : "OK")}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ margin: '8px 0 12px' }}>
                <button className="select-button" onClick={handleBatchProceed}>
                  Übermitteln
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataEntryPage;